import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { cardSubtle, cardTonePill } from "@/lib/card-styles";

import {
  listBuildingServices,
  updateBuildingService,
  type BuildingService,
  type BuildingServiceType,
} from "@/lib/api/building";
import {
  SERVICE_META,
  SERVICE_META_ORDER,
  formatServiceSync,
  serviceOrderIndex,
} from "@/lib/building-services";

interface Props {
  buildingId: string;
  operator: boolean;
}

export function ServicesSection({ buildingId, operator }: Props) {
  const [services, set_services] = useState<BuildingService[]>([]);
  const [saving, set_saving] = useState<BuildingServiceType | null>(null);

  useEffect(() => {
    listBuildingServices(buildingId)
      .then(set_services)
      .catch(() => set_services([]));
  }, [buildingId]);

  // Show every known service type plus anything the backend returns (union), ordered.
  const service_types = useMemo(() => {
    const set = new Set<BuildingServiceType>(SERVICE_META_ORDER);
    for (const s of services) set.add(s.service_type);
    return [...set].sort((a, b) => serviceOrderIndex(a) - serviceOrderIndex(b));
  }, [services]);

  function service_state(
    type: BuildingServiceType,
  ): BuildingService | undefined {
    return services.find((s) => s.service_type === type);
  }

  async function toggle(type: BuildingServiceType, current: boolean) {
    if (!operator || saving) return;
    set_saving(type);
    try {
      const updated = await updateBuildingService(buildingId, type, !current);
      set_services((prev) =>
        prev
          .map((s) => (s.service_type === type ? updated : s))
          .concat(prev.some((s) => s.service_type === type) ? [] : [updated]),
      );
    } finally {
      set_saving(null);
    }
  }

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {service_types.map((type) => {
          const meta = SERVICE_META[type];
          const svc = service_state(type);
          const is_active = svc?.is_active ?? false;
          const is_saving = saving === type;
          return (
            <div
              key={type}
              className={cn(
                cardSubtle,
                "flex flex-col gap-3 p-4",
                is_active && "border-l-[3px] border-l-success",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{meta.label}</p>
                  <p className="text-xs text-muted-foreground/70">
                    {meta.provider}
                  </p>
                </div>
                {operator ? (
                  <button
                    onClick={() => void toggle(type, is_active)}
                    disabled={is_saving}
                    className={cn(
                      "shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50",
                      is_active ? cardTonePill.ok : cardTonePill.empty,
                    )}
                  >
                    {is_saving ? "…" : is_active ? "Aktiivinen" : "Ei käytössä"}
                  </button>
                ) : (
                  <span
                    className={cn(
                      "shrink-0 rounded-full border px-3 py-1 text-xs font-medium",
                      is_active ? cardTonePill.ok : cardTonePill.empty,
                    )}
                  >
                    {is_active ? "Aktiivinen" : "Ei käytössä"}
                  </span>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                {meta.description}
              </p>

              <div className="flex flex-col gap-1 text-xs text-muted-foreground/60">
                <span>
                  <span className="font-medium text-muted-foreground">
                    Signaalit:{" "}
                  </span>
                  {meta.signals}
                </span>
                <span>
                  <span className="font-medium text-muted-foreground">
                    Avaa:{" "}
                  </span>
                  {meta.unlocks}
                </span>
              </div>

              <p className="text-xs text-muted-foreground/50">
                Synkronoitu: {formatServiceSync(svc?.last_synced_at ?? null)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
